import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Download, Mail, Clock, ChevronDown, Gamepad2, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

export function agendaBlockPayload(blocks) {
  return blocks.map((b, i) => ({
    order: b.order ?? i + 1, type: b.type || "", duration_min: parseInt(b.duration_min, 10) || 0,
    title: b.title || "", content: b.content || "", game_id: b.game_id || (b.game && b.game.id) || null,
  }));
}

export default function AgendaTemplateView() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [openB, setOpenB] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/agendas/templates/${tid}`).then(({ data }) => setT(data)).catch(() => toast.error("Could not load agenda"));
  }, [tid]);

  const download = async () => {
    try {
      const res = await api.post("/agendas/render/pdf",
        { title: t.title, meta: t.meta, blocks: agendaBlockPayload(t.blocks) }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `agenda_${(t.title || "class").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/agendas/render/email",
        { title: t.title, meta: t.meta, blocks: agendaBlockPayload(t.blocks), email: email.trim() });
      toast.success(`Agenda emailed to ${data.sent_to}`);
      setEmailOpen(false); setEmail("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const useInBuilder = () => navigate("/agendas/build", { state: { title: t.title, meta: t.meta, blocks: t.blocks } });

  if (!t) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#545454]">Loading…</div>;
  const total = t.blocks.reduce((s, b) => s + (parseInt(b.duration_min, 10) || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="agenda-template-view">
      <Link to="/agendas/library" className="inline-flex items-center gap-1 text-sm font-semibold text-[#920e12] mb-6">
        <ArrowLeft size={16} /> Back to library
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163]">{t.meta}</div>
          <h1 className="mt-1 font-display text-4xl text-[#550001] break-words">{t.title}</h1>
          <p className="mt-1 text-[#545454]">{t.blocks.length} segments · {total} min total run time</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={useInBuilder} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="template-use-btn"><Wand2 size={16} className="mr-1" /> Use in builder</Button>
          <Button onClick={download} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="template-download-btn"><Download size={16} className="mr-1" /> PDF</Button>
          <Button onClick={() => setEmailOpen(true)} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="template-email-btn"><Mail size={16} className="mr-1" /> Email</Button>
        </div>
      </div>

      <ol className="mt-8 space-y-3" data-testid="template-blocks">
        {t.blocks.map((b, i) => {
          const hasDetail = b.content || b.game;
          const open = openB[i];
          return (
            <li key={i} className="bg-white rounded-2xl border border-[#dfd8da] overflow-hidden" data-testid={`template-block-${i}`}>
              <button onClick={() => hasDetail && setOpenB((o) => ({ ...o, [i]: !o[i] }))}
                className={`w-full flex items-start gap-3 p-4 text-left ${hasDetail ? "hover:bg-[#f9f6f6]" : ""}`} aria-expanded={!!open}>
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#920e12] text-white text-sm flex items-center justify-center font-bold">{i + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-sm text-[#545454]"><Clock size={14} /> {b.duration_min} min</span>
                    <span className="text-xs uppercase tracking-wide font-extrabold text-[#bf6163]">{b.type}</span>
                  </span>
                  <span className="block mt-0.5 font-bold text-[#1a1617] break-words">{b.title || b.game?.name || "—"}</span>
                </span>
                {hasDetail && <ChevronDown size={18} className={`shrink-0 mt-1 text-[#920e12] transition-transform ${open ? "rotate-180" : ""}`} />}
              </button>
              {open && hasDetail && (
                <div className="px-4 pb-4 sm:pl-16 space-y-2">
                  {b.game && (
                    <div className="flex items-start gap-2 text-sm">
                      <Gamepad2 size={16} className="text-[#920e12] mt-0.5 shrink-0" />
                      <div>
                        <Link to={`/games/${b.game.id}`} className="font-bold text-[#920e12] underline">{b.game.name}</Link>
                        {b.game.description && <p className="text-[#545454] mt-0.5">{b.game.description}</p>}
                      </div>
                    </div>
                  )}
                  {b.content && <p className="text-sm text-[#1a1617] leading-relaxed whitespace-pre-line">{b.content}</p>}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="template-email-dialog">
          <DialogHeader>
            <DialogTitle>Email this agenda</DialogTitle>
            <DialogDescription>Send to any registered program account (director, participant, or caregiver).</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="tpl-email">Recipient email</Label>
            <input id="tpl-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com"
              data-testid="template-email-input" className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="bg-[#920e12] text-white" data-testid="template-email-send">
              {sending ? <><Loader2 size={16} className="mr-1 animate-spin" /> Sending…</> : <><Mail size={16} className="mr-1" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
